import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getOrderById,
  cancelOrder,
  deliverOrder,
  shipOrder,
} from "../utils/api";

function money(x) {
  const n = Number(x || 0);
  return n.toFixed(2);
}

export default function OrderDetail({ token, currentUser }) {
  const { id } = useParams();

  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [acting, setActing] = useState(false);

  const role = currentUser?.role || "";
  const backTo =
    role === "seller" || role === "admin" ? "/seller-orders" : "/my-orders";
  const isSeller = role === "seller" || role === "admin";
  const isBuyer = role === "buyer" || role === "admin";

  async function doShip() {
    try {
      setErr("");
      setActing(true);
      await shipOrder(token, id);
      const data = await getOrderById(token, id);
      setOrder(data?.order || null);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setErr(e?.message || "Failed to ship order.");
    } finally {
      setActing(false);
    }
  }

  async function doDeliver() {
    try {
      setActing(true);
      await deliverOrder(token, id);
      const data = await getOrderById(token, id);
      setOrder(data?.order || null);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setErr(e?.message || "Failed to deliver order.");
    } finally {
      setActing(false);
    }
  }

  async function doCancel() {
    try {
      setActing(true);
      await cancelOrder(token, id);
      const data = await getOrderById(token, id);
      setOrder(data?.order || null);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setErr(e?.message || "Failed to cancel order.");
    } finally {
      setActing(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setErr("");
      setLoading(true);

      if (!token) {
        setOrder(null);
        setItems([]);
        setLoading(false);
        return;
      }

      try {
        const data = await getOrderById(token, id);
        if (cancelled) return;

        setOrder(data?.order || null);
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        if (!cancelled) setErr("Failed to load order.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token, id]);

  if (!token) {
    return (
      <div className="container pagePadBottom">
        <div className="card">
          <div style={{ fontWeight: 700 }}>Login required</div>
          <div className="muted small" style={{ marginTop: 6 }}>
            Please log in to view order details.
          </div>
          <div style={{ marginTop: 12 }}>
            <Link className="btnPrimary" to="/login">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container pagePadBottom">
      <div className="pageHeader">
        <div className="pageHeaderLeft">
          <div className="h2">Order #{id}</div>
          <div className="muted small" style={{ marginTop: 6 }}>
            Details and items in this order.
          </div>
        </div>
        <div className="pageHeaderRight">
          <Link className="btnSecondary" to={backTo}>
            Back
          </Link>
        </div>
      </div>

      {err ? (
        <div className="alert alertDanger">
          <div className="alertTitle">Error</div>
          <div className="alertBody">{err}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="emptyBox">
          <div className="emptyTitle">Loading…</div>
          <div className="muted small">Fetching order details.</div>
        </div>
      ) : !order ? (
        <div className="emptyBox">
          <div className="emptyTitle">Order not found</div>
          <div className="muted small">This order may not exist.</div>
        </div>
      ) : (
        <>
          <div className="card" style={{ marginTop: 14 }}>
            <div className="cardTitleRow">
              <div className="cardTitle">Summary</div>
              <span className="badge">
                {String(order?.status || "pending").toUpperCase()}
              </span>
            </div>

            <div className="muted small" style={{ marginTop: 8 }}>
              {order?.created_at ? `Placed: ${order.created_at}` : null}
            </div>

            <div className="divider" />
            <div
              className="row"
              style={{ gap: 10, marginTop: 12, flexWrap: "wrap" }}
            >
              {isSeller && order?.status === "pending" ? (
                <button
                  className="btnSecondary"
                  type="button"
                  onClick={doShip}
                  disabled={acting}
                >
                  {acting ? "Working…" : "Mark shipped"}
                </button>
              ) : null}

              {isSeller &&
              (order?.status === "pending" || order?.status === "shipped") ? (
                <button
                  className="btnPrimary"
                  type="button"
                  onClick={doDeliver}
                  disabled={acting}
                >
                  {acting ? "Working…" : "Mark delivered"}
                </button>
              ) : null}

              {isBuyer && order?.status === "pending" ? (
                <button
                  className="btnSecondary"
                  type="button"
                  onClick={doCancel}
                  disabled={acting}
                >
                  {acting ? "Working…" : "Cancel order"}
                </button>
              ) : null}
            </div>

            <div className="row" style={{ justifyContent: "space-between" }}>
              <div style={{ fontWeight: 700 }}>Total</div>
              <div className="priceBlue">
                $
                {money(
                  order?.total_price ?? order?.total ?? order?.order_total ?? 0,
                )}
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <div className="cardTitle">Items</div>
            <div className="divider" />
            {items.length === 0 ? (
              <div className="muted small">No items returned.</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {items.map((it, idx) => {
                  const name = it?.name || "Item";
                  const qty = Number(it?.quantity || 1);
                  const unitPrice = Number(it?.price || 0);
                  const lineTotal = unitPrice * qty;

                  return (
                    <div
                      key={idx}
                      className="row"
                      style={{ justifyContent: "space-between" }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {name}
                        </div>
                        <div className="muted small">Qty: {qty}</div>
                      </div>
                      <div className="priceBlue">${money(lineTotal)}</div>
                      <div className="muted small">
                        Qty: {qty}
                        {qty > 1 ? ` • Each: $${money(unitPrice)}` : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
